import React from 'react';
import { IconButton, Tooltip, alpha, styled } from '@mui/material';
import { Hub as GraphIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Message } from '../../stores/chatStore';

/**
 * Props for ViewInGraphButton
 */
interface ViewInGraphButtonProps {
  /**
   * Message to view in graph
   */
  message: Message;

  /**
   * Optional custom tooltip
   */
  tooltip?: string;

  /**
   * Optional size
   */
  size?: 'small' | 'medium';
}

/**
 * Styled icon button with graph theme
 */
const GraphButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.15),
  },
}));

/**
 * ViewInGraphButton Component
 *
 * Contextual button that takes users to the Knowledge Graph
 * with this message focused and highlighted.
 *
 * Week 5 Feature: Graph discovery and accessibility
 */
const ViewInGraphButton: React.FC<ViewInGraphButtonProps> = ({
  message,
  tooltip = 'View in Knowledge Graph',
  size = 'small',
}) => {
  const navigate = useNavigate();

  const handleViewInGraph = () => {
    // Navigate to graph with nodeId parameter to focus this message
    // The graph page will automatically select and focus this node
    navigate(`/knowledge-graph?nodeId=${message.id}`);
  };

  return (
    <Tooltip title={tooltip} placement="top">
      <GraphButton size={size} onClick={handleViewInGraph} color="secondary">
        <GraphIcon fontSize="small" />
      </GraphButton>
    </Tooltip>
  );
};

export default ViewInGraphButton;
